import { Fragment, useEffect, useMemo, useRef, useState } from 'react';

import {
  ChakraBox,
  ChakraFlex,
  ChakraHStack,
  ChatUser,
} from '@equip.health/ui';
import { GroupChannel } from '@sendbird/chat/groupChannel';
import { useHistory, useLocation } from 'react-router-dom';

import OverviewLoading from '~/components/common/OverviewLoading';
import { useGlobalContext } from '~/lib/contexts/GlobalContext';
import useChatProxy from '~/lib/hooks/chat/useChatProxy';
import { ChatMessage } from '~/lib/types/chat';
import {
  ChatChannelTab,
  getChannelName,
} from '~/lib/util/chat/chatChannel.util';
import {
  getChannelTabIndexToChannelsMapper,
  getDefaultChannel,
  onChannelTabChangeHandler,
} from '~/lib/util/patientChannels.util';
import { getValueFromQueryParams } from '~/lib/utils';
import { Path } from '~/routes/constants';

import ChannelListPanel from './ChannelListPanel';
import ChatOutOfOfficeActionBarList from './ChatOutOfOfficeActionBarList';
import EmptyChannel from './EmptyChannel';
import MessagesHeader from './MessagesHeader';
import PatientOtherMessagesPanel from './PatientOtherMessagesPanel';
import { PATIENT_MESSAGES_SIDEBAR_WIDTH } from '~/lib/constants/patients.constants';

const idPrefix = 'messages';
const idPostfix = 'tab';

const generateId = (section: string, userId: string) =>
  `${idPrefix}-${section}__${userId}__${idPostfix}`;

interface PatientOtherChannelsProps {
  patientFirstName: string;
  patientId: string;
  selectedChannelType: string;
  channelTypeOptions: UniversalSelectOption[];
  onChannelTypeSelect: (newChannelType: string) => void;
  universalStatusCode?: string;
}

const PatientOtherChannels = ({
  patientFirstName,
  patientId,
  selectedChannelType,
  channelTypeOptions,
  onChannelTypeSelect,
  universalStatusCode,
}: PatientOtherChannelsProps) => {
  const { currentUser } = useGlobalContext();
  const [selectedChannel, setSelectedChannel] = useState<GroupChannel>(null);
  const [isNoChannel, setIsNoChannel] = useState<boolean>(false);
  const [channelTabIndex, setChannelTabIndex] = useState<number>(0);
  const textBoxRef = useRef<HTMLInputElement>(null);
  const replyTextBoxRef = useRef<HTMLInputElement>(null);

  const {
    channels,
    chatMessages,
    getReplies,
    hasMoreMessages,
    isChannelReadOnly,
    isGroupChannel,
    loadChannels,
    loadMessages,
  } = useChatProxy({
    channel: selectedChannel,
    chatUserId: currentUser.userId,
    onChannelUpdated: () => {},
    patientId,
    universalStatusCode,
  });

  const messageListPanelRef = useRef<HTMLDivElement>(null);

  const { search } = useLocation();
  const channelUrl = getValueFromQueryParams(search, 'channelUrl');
  const history = useHistory();

  const getUnreadCount = (channels: GroupChannel[]): number =>
    channels.reduce(
      (count: number, thisChannel: GroupChannel) =>
        thisChannel.unreadMentionCount + thisChannel.unreadMessageCount + count,
      0,
    );

  const channelTabIndexToChannelsMapper =
    getChannelTabIndexToChannelsMapper(channels);

  const selectedTabChannels = useMemo(
    () => channelTabIndexToChannelsMapper[channelTabIndex],
    [channels, channelTabIndex],
  );

  const onChannelTabChange = (tab: number) => {
    onChannelTabChangeHandler({
      channels,
      channelTabIndex,
      history,
      patientId,
      setChannelTabIndex,
      setSelectedChannel,
      tab,
    });
  };

  useEffect(() => {
    // Will stay on INTERNAL tab if it has channels, or attempt to show EXTERNAL tab.
    // Finally, outside of the useEffect, shows EmptyChannel if there is no channels.
    if (!selectedChannel && selectedTabChannels?.length > 0) {
      setSelectedChannel(
        selectedTabChannels.find((channel) => channel.url === channelUrl) ??
          getDefaultChannel(selectedTabChannels),
      );
    } else if (
      channels.length > 0 &&
      !channelUrl &&
      channelTabIndex === ChatChannelTab.INTERNAL &&
      selectedTabChannels.length === 0
    ) {
      onChannelTabChange(ChatChannelTab.EXTERNAL);
    }
  }, [selectedTabChannels, channelUrl]);

  useEffect(() => {
    loadChannels(() => {
      setIsNoChannel(true);
    });
  }, []);

  const fileMessages = useMemo<ChatMessage[]>(() => {
    const output = [];
    chatMessages?.forEach((message) => {
      if (message.isFileMessage && !message.isRemoved) {
        output.push(message);
      }

      if (message.thread?.length > 0) {
        message.thread?.forEach((reply) => {
          if (reply.isFileMessage && !reply.isRemoved) {
            output.push(reply);
          }
        });
      }
    });
    return output;
  }, [chatMessages]);

  const containerRef = useRef<HTMLDivElement>(null);

  const currentSelectedChannel = useMemo(() => {
    if (textBoxRef.current) textBoxRef.current.focus();
    return (
      selectedTabChannels?.find(
        (channel) => channel.url === selectedChannel?.url,
      ) ?? selectedChannel
    );
  }, [channelTabIndex, selectedChannel]);

  const logDebugChannelIsReadOnly = () => console.debug('channel is read only');

  if (isNoChannel)
    return (
      <EmptyChannel
        channelTypeOptions={channelTypeOptions}
        id="empty-channel"
        onChannelTypeSelect={onChannelTypeSelect}
        patientId={patientId}
        selectedChannelType={selectedChannelType}
      />
    );

  if (!channels || !selectedChannel) return <OverviewLoading />;

  return (
    <Fragment>
      <ChakraFlex
        alignItems="stretch"
        direction="column"
        id={generateId('messages-tab-container', currentUser.userId)}
        marginBottom="2px"
        paddingRight="16px"
        width="100%"
      >
        <MessagesHeader
          channelAttachments={fileMessages}
          channelMembers={currentSelectedChannel?.members as ChatUser[]}
          channelTabIndex={channelTabIndex}
          channelTitle={getChannelName(currentSelectedChannel)}
          channelTypeOptions={channelTypeOptions}
          id={generateId('messages-header', currentUser.userId)}
          isChannelReadOnly={isChannelReadOnly}
          onChannelTabChange={onChannelTabChange}
          onChannelTypeSelect={onChannelTypeSelect}
          onMessageRemoved={logDebugChannelIsReadOnly}
          selectedChannelType={selectedChannelType}
          unreadCount={{
            [ChatChannelTab.EXTERNAL]: getUnreadCount(
              channelTabIndexToChannelsMapper[ChatChannelTab.EXTERNAL],
            ),
            [ChatChannelTab.INTERNAL]: getUnreadCount(
              channelTabIndexToChannelsMapper[ChatChannelTab.INTERNAL],
            ),
          }}
        />
      </ChakraFlex>
      <ChakraFlex
        flex={1}
        gridGap="48px"
        height="100%"
        id={generateId('channels-container', currentUser.userId)}
        overflowY="hidden"
        ref={containerRef}
        width="100%"
      >
        <ChakraBox
          flex="0 0 auto"
          id={generateId('channels-wrapper', currentUser.userId)}
          overflowY="auto"
          width={PATIENT_MESSAGES_SIDEBAR_WIDTH}
        >
          <ChannelListPanel
            channels={selectedTabChannels ?? []}
            id={generateId('channels', currentUser.userId)}
            onChannelSelected={(url) => {
              if (currentSelectedChannel?.url !== url) {
                history.push(
                  `${Path.MAUD_PATIENTS}/${patientId}/messages?channelUrl=${url}`,
                );
                setSelectedChannel(() =>
                  selectedTabChannels?.find((channel) => channel.url === url),
                );
              }
            }}
            patientName={patientFirstName}
            selectedChannelUrl={currentSelectedChannel?.url}
          />
        </ChakraBox>
        <ChakraFlex
          direction="column"
          flex={1}
          id={generateId('messages-container', currentUser.userId)}
        >
          <ChakraBox
            data-testid={generateId('messages-wrapper', currentUser.userId)}
            height="100%"
            id={generateId('messages-wrapper', currentUser.userId)}
            overflowY="auto"
            position="relative"
            width="100%"
          >
            <PatientOtherMessagesPanel
              channel={selectedChannel}
              chatUserId={currentUser.userId}
              hasMoreMessages={hasMoreMessages}
              id={generateId('messages-list', currentUser.userId)}
              isChannelReadOnly={isChannelReadOnly}
              isGroupChannel={isGroupChannel}
              isMessageSent={false}
              loadMoreMessages={loadMessages}
              markMessagesAsRead={logDebugChannelIsReadOnly}
              messages={chatMessages?.filter((message) => !message.isRemoved)}
              onInputFocus={(inputTextRef) => {
                replyTextBoxRef.current = inputTextRef.current;
              }}
              onMessageRemoved={logDebugChannelIsReadOnly}
              onRepliesExpanded={(selectedMessage) =>
                getReplies(selectedMessage)
              }
              onSendMessage={logDebugChannelIsReadOnly}
              patientId={patientId}
              ref={messageListPanelRef}
            />
          </ChakraBox>
          <ChakraHStack flexDirection="column" gap={0} marginBottom="8px">
            <ChatOutOfOfficeActionBarList selectedChannel={selectedChannel} />
          </ChakraHStack>
        </ChakraFlex>
      </ChakraFlex>
    </Fragment>
  );
};

export default PatientOtherChannels;
